import axiosConfig from "./axiosConfig";
import { PeopleRole } from "../models/peopleCandidate";

// One fact the drafter was allowed to phrase, shown in the modal's "Data used to
// write this" inspector with an include / exclude checkbox. `included: false` means
// the operator unticked it, so it was NOT rendered into the model's prompt on this
// turn (the cheap, deterministic control). The full candidate set always comes back
// - excluded items stay in the list so they can be re-ticked - so the checkboxes
// bind directly to `included`.
export interface OutreachInputItem {
  id: string;
  category: string;
  label: string;
  value: string;
  included: boolean;
  // Only the site-opportunity + proximity facts are toggleable; the recipient +
  // company facts are standard (always included, rendered without a checkbox).
  toggleable: boolean;
}

// Draft (or re-draft) a cold-outreach email for a lead's contact. A first call omits
// `outreachMessageId` and starts a thread; a rerun passes it back with the operator's
// `excludedInputIds` (unticked facts) and / or free-text `feedback`, appending a new
// turn to the same thread. `apolloId` picks which enriched contact to write to (the
// first is used if omitted).
export interface OutreachDraftRequest {
  role: PeopleRole;
  apolloId?: string | null;
  outreachMessageId?: string | null;
  excludedInputIds?: string[];
  feedback?: string | null;
  // How much of the (real) nearby-operational-site data the email may use as social
  // proof: "area_distance" (default - distance + area, don't name it),
  // "area_distance_size", "named" (name + exact distance), or "vague". The BE renders
  // the proximity fact at this level; softer levels never expose the site name.
  proximityDisclosure?: string | null;
  // The Kinde user email, for the "drafted by" audit label (BE falls back to the
  // access-token identity, which is the opaque kp_ sub - never a human email).
  userEmail?: string | null;
}

export interface OutreachDraftResponse {
  outreachMessageId: string;
  turnIndex: number;
  subject: string;
  body: string;
  inputs: OutreachInputItem[];
  traceRunId: string;
  modelId: string;
  inputTokens: number;
  outputTokens: number;
}

const postOutreachDraft = async (
  authToken: string,
  leadId: string,
  body: OutreachDraftRequest
): Promise<OutreachDraftResponse> => {
  const { data } = await axiosConfig.post(
    `/leads/${leadId}/outreach/draft`,
    body,
    { headers: { Authorization: `Bearer ${authToken}` } }
  );
  return data;
};

export default postOutreachDraft;
